import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import AdminLayout from "../layouts/adminLayout/AdminLayout";
import {
  createThana,
  deleteThana,
  getCityCorporations,
  getDivisions,
  getThanas,
  normalizeList,
  updateThana,
} from "../config/api/locationApi";

const emptyForm = {
  division_id: "",
  city_corporation_id: "",
  name: "",
  bn_name: "",
  url: "",
};

export default function ThanaList() {
  const [rows, setRows] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [cityCorporations, setCityCorporations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filterDivisionId, setFilterDivisionId] = useState("");
  const [filterCityCorporationId, setFilterCityCorporationId] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const filteredCityCorporations = useMemo(() => {
    if (!filterDivisionId) return cityCorporations;
    return cityCorporations.filter(
      (item) => String(item.division_id) === String(filterDivisionId),
    );
  }, [cityCorporations, filterDivisionId]);

  const formCityCorporations = useMemo(() => {
    if (!formData.division_id) return [];
    return cityCorporations.filter(
      (item) => String(item.division_id) === String(formData.division_id),
    );
  }, [cityCorporations, formData.division_id]);

  const fetchOptions = useCallback(async () => {
    try {
      const [divisionRes, cityCorporationRes] = await Promise.all([
        getDivisions(),
        getCityCorporations(),
      ]);
      setDivisions(normalizeList(divisionRes));
      setCityCorporations(normalizeList(cityCorporationRes));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load location options");
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getThanas({
        city_corporation_id: filterCityCorporationId || undefined,
        search: appliedSearch || undefined,
      });
      setRows(normalizeList(res));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load thanas");
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, filterCityCorporationId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleSearch = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedSearch(search);
  };

  const handleFilterDivisionChange = (value) => {
    setFilterDivisionId(value);
    setFilterCityCorporationId("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenCreate = () => {
    setEditId(null);
    setFormData(emptyForm);
    setOpenModal(true);
  };

  const handleOpenEdit = (row) => {
    const cityCorporation = cityCorporations.find(
      (item) => String(item.id) === String(row.city_corporation_id),
    );

    setEditId(row.id);
    setFormData({
      division_id: cityCorporation?.division_id || "",
      city_corporation_id: row.city_corporation_id || "",
      name: row.name || "",
      bn_name: row.bn_name || "",
      url: row.url || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditId(null);
    setFormData(emptyForm);
  };

  const handleFormDivisionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      division_id: value,
      city_corporation_id: "",
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.city_corporation_id || !formData.name.trim()) {
        toast.error("City corporation and name are required");
        return;
      }

      const payload = {
        city_corporation_id: formData.city_corporation_id,
        name: formData.name,
        bn_name: formData.bn_name,
        url: formData.url,
      };

      if (editId) {
        await updateThana({ id: editId, ...payload });
        toast.success("Thana updated successfully");
      } else {
        await createThana(payload);
        toast.success("Thana created successfully");
      }

      handleCloseModal();
      fetchRows();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save thana");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete?")) return;

    try {
      await deleteThana(id);
      toast.success("Thana deleted successfully");
      fetchRows();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete thana");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "name", headerName: "Name", width: 180 },
    {
      field: "bn_name",
      headerName: "Bangla Name",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "city_corporation_id",
      headerName: "City Corporation",
      width: 200,
      renderCell: (params) =>
        cityCorporations.find(
          (item) => String(item.id) === String(params.value),
        )?.name ||
        params.value ||
        "-",
    },
    {
      field: "url",
      headerName: "URL",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box className="flex gap-2">
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenEdit(params.row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout title="Thana">
      <Box className="p-6 bg-white !rounded">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-5">
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Select
            size="small"
            displayEmpty
            value={filterDivisionId}
            onChange={(e) => handleFilterDivisionChange(e.target.value)}
          >
            <MenuItem value="">All Divisions</MenuItem>
            {divisions.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            displayEmpty
            value={filterCityCorporationId}
            onChange={(e) => {
              setFilterCityCorporationId(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            disabled={!filterDivisionId}
          >
            <MenuItem value="">All City Corporations</MenuItem>
            {filteredCityCorporations.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outlined" onClick={handleOpenCreate}>
            Add Thana
          </Button>
        </div>

        <div style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
          />
        </div>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{editId ? "Edit Thana" : "Add Thana"}</DialogTitle>
          <DialogContent>
            <Box className="grid grid-cols-1 gap-4 mt-3">
              <Select
                size="small"
                displayEmpty
                value={formData.division_id}
                onChange={(e) => handleFormDivisionChange(e.target.value)}
              >
                <MenuItem value="">Select Division</MenuItem>
                {divisions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                displayEmpty
                value={formData.city_corporation_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    city_corporation_id: e.target.value,
                  }))
                }
                disabled={!formData.division_id}
              >
                <MenuItem value="">Select City Corporation</MenuItem>
                {formCityCorporations.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                label="Thana Name"
                size="small"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
              />

              <TextField
                label="Bangla Name"
                size="small"
                value={formData.bn_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, bn_name: e.target.value }))
                }
              />

              <TextField
                label="URL"
                size="small"
                value={formData.url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, url: e.target.value }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}
